import {
  BoldItalicUnderlineToggles,
  headingsPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  type MDXEditorMethods,
  quotePlugin,
  toolbarPlugin,
  UndoRedo,
  BlockTypeSelect,
  Separator,
} from "@mdxeditor/editor";
import { FC, useEffect } from "react";
import "@mdxeditor/editor/style.css";
import "./MdxEditorComponent.css";
import { Match } from "../../../../hooks/useFindReplace";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useAppDispatch";
import { updateBlock } from "../../../../redux/JobModule/QCDetailPage/QCDetailPageAction";
import { eliminateLinesAndSymbols } from "../../../../utils/eliminateInvalidLinesInDictationFile";

interface EditorProps {
  markdown: string;
  editorRef?: React.MutableRefObject<MDXEditorMethods | null>;
  disabled: boolean;
  allMatches: Match[];
  currentMatchIndex: number;
  packId: number;
  texts: any[];
}

const Editor: FC<EditorProps> = ({
  markdown,
  editorRef,
  disabled,
  allMatches,
  currentMatchIndex,
  texts,
}) => {
  const idTranscription = useSelector(
    (state: RootState) =>
      state.qCDetailPageState.data.transcriptions[0].idTranscription
  );

  const dispatch = useAppDispatch();

  const cleanBaseText = eliminateLinesAndSymbols(markdown);

  // AUTO SCROLL
  const scrollToHighlight = () => {
    setTimeout(() => {
      const el = document.querySelector(".highlighted") as HTMLElement;
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 50);
  };

  useEffect(() => {
    if (!editorRef?.current) return;

    const editor = editorRef.current;
    let text = cleanBaseText;
    if (!allMatches.length) {
      editor.setMarkdown(text);
      return;
    }

    const match = allMatches[currentMatchIndex];
    if (!match) return;

    const before = text.slice(0, match.start);
    const matchText = text.slice(match.start, match.end);
    const after = text.slice(match.end);

    const highlighted =
      before +
      `<span class="highlighted">${matchText}</span>` +
      after;

    editor.setMarkdown(highlighted);

    scrollToHighlight();
  }, [allMatches, currentMatchIndex, markdown]);

  return (
    <div className="d-flex flex-column overflow-hidden">
      <div className="markdownEditor">
        <MDXEditor
          ref={editorRef}
          markdown={cleanBaseText}
          readOnly={disabled}
          contentEditableClassName="prose"
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <Separator />
                  <BoldItalicUnderlineToggles />
                  <Separator />
                  <ListsToggle />
                  <Separator />
                  <BlockTypeSelect />
                </>
              ),
            }),
          ]}
        />
      </div>

      <div className="d-flex w-100">
        <button
          className="button"
          style={{
            padding: "10px 20px",
            marginBottom: "5px",
            alignSelf: "flex-end",
          }}
          disabled={disabled}
          onClick={() => {
            if (!editorRef?.current) return;

            const newBlock = {
              TranscriptionId: idTranscription,
              TranscriptionBlocks: [
                {
                  TranscriptionBlockId: texts[0].idTranscriptionBlock,
                  StartTimeSec: texts[0].startTimeSec,
                  EndTimeSec: texts[0].endTimeSec,
                  Speaker: texts[0].speaker,
                  Text: eliminateLinesAndSymbols(
                    editorRef.current
                      .getMarkdown()
                      .replace(/<span class="highlighted">/g, "")
                      .replace(/<\/span>/g, "")
                  ),
                },
              ],
            };

            dispatch(
              updateBlock({
                id: idTranscription,
                data: newBlock,
                blockIndex: 0,
              })
            );
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default Editor;